import { useState, useEffect } from 'react';
import { api } from '../api/client';
import {
  cacheCompanyModules,
  readCachedCompanyModules,
  normalizeModules,
  resolveModuleCacheKey,
} from '../utils/companyModules';

/**
 * Loads per-company module flags without flashing disabled nav items.
 * Uses sessionStorage cache + optional seed from login user object.
 */
export function useCompanyModules({ slug, companyId, authenticated = false, seed = null } = {}) {
  const cacheKey = resolveModuleCacheKey({ slug, companyId });
  const cached = cacheKey ? readCachedCompanyModules(cacheKey) : null;
  const seeded = seed ? normalizeModules(seed) : null;
  const initial = seeded || cached;

  const [modules, setModules] = useState(() => initial);
  const [ready, setReady] = useState(() => !!initial);
  const hadInitial = !!initial;

  useEffect(() => {
    if (!authenticated && !slug) {
      setReady(true);
      return undefined;
    }

    let cancelled = false;

    async function load() {
      try {
        const data = authenticated
          ? await api.getCompanySettings()
          : await api.getCompanyBySlug(slug);
        if (cancelled || !data) return;
        const normalized = normalizeModules(data);
        setModules(normalized);
        if (cacheKey) cacheCompanyModules(cacheKey, normalized);
      } catch {
        if (!cancelled && !hadInitial) {
          setModules({ riders_enabled: 0, branches_enabled: 0, dispatch_enabled: 0, merchant_shipping_enabled: 0 });
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [slug, companyId, authenticated, cacheKey, hadInitial]);

  return { modules, ready };
}